import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Pencil } from 'lucide-react';
import type {
  CrmAccountDetail as CrmAccountDetailDto,
  CrmActivity,
  CrmContact,
} from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ContractorDocuments } from '@/components/contractors/ContractorDocuments';
import { useAuth } from '@/hooks/useAuth';
import { useTabParam } from '@/hooks/useTabParam';
import { hasModuleAccess } from '@/lib/permissions';
import { useCrmApi } from '@/services/api';
import { t } from '@/i18n';
import type { AppShellContext } from '@/components/layout/AppShell';
import { AccountFormDialog } from './AccountFormDialog';
import { AccountOverviewTab } from './AccountOverviewTab';
import { ContactFormDialog } from './ContactFormDialog';
import { useCreateContractorFromAccount } from './useCreateContractorFromAccount';

/** `?tab=documents` deep-links the documents pane (the reminder bell does). */
const ACCOUNT_TABS = ['overview', 'documents'] as const;
type AccountTab = (typeof ACCOUNT_TABS)[number];

export function AccountDetail() {
  const { nip } = useOutletContext<AppShellContext>();
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const { getAccount, listActivities, createActivity } = useCrmApi();

  const [account, setAccount] = useState<CrmAccountDetailDto | null>(null);
  const [activities, setActivities] = useState<CrmActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  /** null while adding, the row being edited otherwise. */
  const [editedContact, setEditedContact] = useState<CrmContact | null>(null);

  // Zakładka Dokumenty woła endpointy dokumentów kontrahenta, które wymagają
  // modułu 'contractors' — konto z samym modułem 'crm' (np. rola user) nie
  // ma do nich dostępu i dostałoby 403.
  const canSeeDocuments = hasModuleAccess(permissions, nip, 'contractors');

  const [, setSearchParams] = useSearchParams();
  const [tabParam, setTab] = useTabParam(ACCOUNT_TABS, 'overview');
  // Bez modułu 'contractors' zakładki Dokumenty w ogóle nie ma — również
  // wtedy, gdy ktoś wkleił `?tab=documents`.
  const tab: AccountTab = tabParam === 'documents' && !canSeeDocuments ? 'overview' : tabParam;

  const load = useCallback(async () => {
    if (!accountId) return;
    try {
      const [detail, timeline] = await Promise.all([
        getAccount(nip, accountId),
        listActivities(nip, { accountId, limit: 100 }),
      ]);
      setAccount(detail);
      setActivities(timeline);
    } catch {
      toast.error(t('errors.loadingFailed'));
    } finally {
      setLoading(false);
    }
  }, [nip, accountId, getAccount, listActivities]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const {
    canCreate: canManageContractor,
    creating: creatingContractor,
    handleCreate: createContractorAndOpenUpload,
  } = useCreateContractorFromAccount(nip, accountId ?? '', {
    onCreated: () => {
      load();
      // Kontrahent już istnieje — zostajemy na zakładce Dokumenty i od razu
      // otwieramy okno wgrywania (`?upload=1` czyta ContractorDocuments i
      // natychmiast kasuje ten parametr).
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'documents');
          next.set('upload', '1');
          return next;
        },
        { replace: true },
      );
    },
  });

  const handleAddNote = async () => {
    if (!accountId || note.trim() === '') return;
    setSavingNote(true);
    try {
      await createActivity(nip, { type: 'note', body: note.trim(), accountId });
      setNote('');
      load();
    } catch {
      toast.error(t('errors.saveFailed'));
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton key={index} className="h-5 w-full" />
        ))}
      </div>
    );
  }

  if (!account) {
    return <p className="text-muted-foreground text-sm">{t('errors.notFound')}</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/${nip}/crm/klienci`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">{account.name}</h1>
        <Badge variant="secondary">{t(`crm.accountStatus.${account.status}`)}</Badge>
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => setEditOpen(true)}>
          <Pencil className="mr-2 h-4 w-4" />
          {t('common.edit')}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as AccountTab)}>
        <TabsList>
          <TabsTrigger value="overview">{t('crm.accounts.tabs.overview')}</TabsTrigger>
          {canSeeDocuments ? (
            <TabsTrigger value="documents">{t('crm.accounts.tabs.documents')}</TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="overview" className="pt-2">
          <AccountOverviewTab
            nip={nip}
            account={account}
            activities={activities}
            note={note}
            onNoteChange={setNote}
            savingNote={savingNote}
            onAddNote={handleAddNote}
            onAddContact={() => {
              setEditedContact(null);
              setContactDialogOpen(true);
            }}
            onEditContact={(contact) => {
              setEditedContact(contact);
              setContactDialogOpen(true);
            }}
            onChanged={load}
          />
        </TabsContent>

        {canSeeDocuments ? (
          <TabsContent value="documents" className="pt-2">
            {account.contractor ? (
              <ContractorDocuments companyNip={nip} contractorId={account.contractor.id} />
            ) : (
              <Card>
                <CardContent className="space-y-3 pt-6 text-sm">
                  <p className="text-muted-foreground">{t('crm.accounts.documents.noContractorHint')}</p>
                  {canManageContractor ? (
                    <Button size="sm" onClick={createContractorAndOpenUpload} disabled={creatingContractor}>
                      {t('crm.accounts.documents.createAndUpload')}
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ) : null}
      </Tabs>

      <AccountFormDialog
        nip={nip}
        open={editOpen}
        account={account}
        onOpenChange={setEditOpen}
        onSaved={() => {
          setEditOpen(false);
          load();
        }}
      />

      <ContactFormDialog
        nip={nip}
        accountId={account.id}
        contact={editedContact}
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        onSaved={() => {
          setContactDialogOpen(false);
          load();
        }}
      />
    </div>
  );
}
